/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(initialState: { currentUser?: any } | undefined) {
  const { currentUser } = initialState ?? {};
  const permissions = currentUser?.permissions || [];
  // Create/Update/Delete are disabled site-wide for now (view-only mode) — Read stays enabled.
  const hasPermission = (resource: string, scope: string) => scope === 'Read';
    /* permissions.some((p: any) => p.rsname === resource && p.scopes?.includes(scope)); */

  // Group-level access permissions - check if user has access to any child menu
  const hasAnyChildAccess = (childPermissions: string[]) => true;
    /* childPermissions.some(permission =>
      permissions.some((p: any) => p.rsname === permission && p.scopes?.includes('Read'))
    ); */

  // Pages below are hidden — not consumed by FabricPLM IED and outside the requested keep-list
  // (Customer, Category, SubCategory, ProductGroup, MaterialType, OperationLibrary,
  // OperationLibraryDetail, Product, LightSource, Employee, MachineType, Section stay visible).
  return {
    // Group access permissions
    canViewMasterData: hasAnyChildAccess(['Brand', 'Color', 'Currency', 'Origin', 'Size', 'Uom', 'Site', 'AccountGroup']),
    canViewOrganizationGroup: false,
    canViewProduction: hasAnyChildAccess(['Line', 'Machine', 'MachineType', 'OperationLibrary', 'OperationLibraryDetail', 'Component', 'ComponentGroup']),
    canViewBusinessPartners: hasAnyChildAccess(['Customer', 'CustomerType', 'Supplier', 'SupplierType']),
    canViewProductManagement: hasAnyChildAccess(['Product', 'Category', 'ProductGroup', 'SubCategory', 'MaterialType']),
    canViewHumanResources: hasAnyChildAccess(['Employee', 'EmployeeType', 'Calendar', 'CalendarCustomize', 'Holiday']),
    canViewShippingLogistics: false,

    // Individual page access permissions
    canViewOrganization: false,
    canCreateOrganization: hasPermission('Organization', 'Create'),
    canUpdateOrganization: hasPermission('Organization', 'Update'),
    canDeleteOrganization: hasPermission('Organization', 'Delete'),

    canViewOrganizationDetail: false,
    canCreateOrganizationDetail: hasPermission('OrganizationDetail', 'Create'),
    canUpdateOrganizationDetail: hasPermission('OrganizationDetail', 'Update'),
    canDeleteOrganizationDetail: hasPermission('OrganizationDetail', 'Delete'),

    canViewOperationLibraryDetail: hasPermission('OperationLibraryDetail', 'Read'),
    // Update re-enabled for OperationLibraryDetail specifically — Create/Delete stay disabled site-wide.
    canCreateOperationLibraryDetail: hasPermission('OperationLibraryDetail', 'Create'),
    canUpdateOperationLibraryDetail: true,
    canDeleteOperationLibraryDetail: hasPermission('OperationLibraryDetail', 'Delete'),

    canViewAddressDetail: false,

    canViewAccountGroup: hasPermission('AccountGroup', 'Read'),
    canCreateAccountGroup: hasPermission('AccountGroup', 'Create'),
    canUpdateAccountGroup: hasPermission('AccountGroup', 'Update'),
    canDeleteAccountGroup: hasPermission('AccountGroup', 'Delete'),

    canViewBrand: false,
    canCreateBrand: hasPermission('Brand', 'Create'),
    canUpdateBrand: hasPermission('Brand', 'Update'),
    canDeleteBrand: hasPermission('Brand', 'Delete'),

    canViewColor: false,
    canCreateColor: hasPermission('Color', 'Create'),
    canUpdateColor: hasPermission('Color', 'Update'),
    canDeleteColor: hasPermission('Color', 'Delete'),

    canViewCurrency: false,
    canCreateCurrency: hasPermission('Currency', 'Create'),
    canUpdateCurrency: hasPermission('Currency', 'Update'),
    canDeleteCurrency: hasPermission('Currency', 'Delete'),

    canViewCustomer: hasPermission('Customer', 'Read'),
    canCreateCustomer: hasPermission('Customer', 'Create'),
    canUpdateCustomer: hasPermission('Customer', 'Update'),
    canDeleteCustomer: hasPermission('Customer', 'Delete'),

    canViewCustomerType: false,
    canCreateCustomerType: hasPermission('CustomerType', 'Create'),
    canUpdateCustomerType: hasPermission('CustomerType', 'Update'),
    canDeleteCustomerType: hasPermission('CustomerType', 'Delete'),

    canViewAddress: false,
    canCreateAddress: hasPermission('Address', 'Create'),
    canUpdateAddress: hasPermission('Address', 'Update'),
    canDeleteAddress: hasPermission('Address', 'Delete'),

    canViewCalendarCustomize: false,
    canCreateCalendarCustomize: hasPermission('CalendarCustomize', 'Create'),
    canUpdateCalendarCustomize: hasPermission('CalendarCustomize', 'Update'),
    canDeleteCalendarCustomize: hasPermission('CalendarCustomize', 'Delete'),

    canViewCalendar: false,
    canCreateCalendar: hasPermission('Calendar', 'Create'),
    canUpdateCalendar: hasPermission('Calendar', 'Update'),
    canDeleteCalendar: hasPermission('Calendar', 'Delete'),

    canViewDeliveryTerm: false,
    canCreateDeliveryTerm: hasPermission('DeliveryTerm', 'Create'),
    canUpdateDeliveryTerm: hasPermission('DeliveryTerm', 'Update'),
    canDeleteDeliveryTerm: hasPermission('DeliveryTerm', 'Delete'),

    canViewEmployee: hasPermission('Employee', 'Read'),
    // Create re-enabled for Employee specifically — Update/Delete stay disabled site-wide.
    canCreateEmployee: true,
    canUpdateEmployee: hasPermission('Employee', 'Update'),
    canDeleteEmployee: hasPermission('Employee', 'Delete'),

    // Added missing page permissions
    canViewEmployeeType: hasPermission('EmployeeType', 'Read'),
    canCreateEmployeeType: hasPermission('EmployeeType', 'Create'),
    canUpdateEmployeeType: hasPermission('EmployeeType', 'Update'),
    canDeleteEmployeeType: hasPermission('EmployeeType', 'Delete'),

    canViewHoliday: false,
    canCreateHoliday: hasPermission('Holiday', 'Create'),
    canUpdateHoliday: hasPermission('Holiday', 'Update'),
    canDeleteHoliday: hasPermission('Holiday', 'Delete'),

    canViewLine: false,
    canCreateLine: hasPermission('Line', 'Create'),
    canUpdateLine: hasPermission('Line', 'Update'),
    canDeleteLine: hasPermission('Line', 'Delete'),

    canViewMachine: false,
    canCreateMachine: hasPermission('Machine', 'Create'),
    canUpdateMachine: hasPermission('Machine', 'Update'),
    canDeleteMachine: hasPermission('Machine', 'Delete'),

    canViewMachineType: hasPermission('MachineType', 'Read'),
    canCreateMachineType: hasPermission('MachineType', 'Create'),
    canUpdateMachineType: hasPermission('MachineType', 'Update'),
    canDeleteMachineType: hasPermission('MachineType', 'Delete'),

    canViewOperationLibrary: hasPermission('OperationLibrary', 'Read'),
    // Create/Update re-enabled for OperationLibrary specifically — Delete stays disabled site-wide.
    canCreateOperationLibrary: true,
    canUpdateOperationLibrary: true,
    canDeleteOperationLibrary: hasPermission('OperationLibrary', 'Delete'),

    canViewOrigin: false,
    canCreateOrigin: hasPermission('Origin', 'Create'),
    canUpdateOrigin: hasPermission('Origin', 'Update'),
    canDeleteOrigin: hasPermission('Origin', 'Delete'),

    canViewPaymentTerm: false,
    canCreatePaymentTerm: hasPermission('PaymentTerm', 'Create'),
    canUpdatePaymentTerm: hasPermission('PaymentTerm', 'Update'),
    canDeletePaymentTerm: hasPermission('PaymentTerm', 'Delete'),

    canViewPort: false,
    canCreatePort: hasPermission('Port', 'Create'),
    canUpdatePort: hasPermission('Port', 'Update'),
    canDeletePort: hasPermission('Port', 'Delete'),

    canViewProduct: hasPermission('Product', 'Read'),
    // Update re-enabled for Product specifically — Create/Delete stay disabled site-wide.
    canCreateProduct: hasPermission('Product', 'Create'),
    canUpdateProduct: true,
    canDeleteProduct: hasPermission('Product', 'Delete'),

    canViewProductCategory: false,
    canCreateProductCategory: hasPermission('ProductCategory', 'Create'),
    canUpdateProductCategory: hasPermission('ProductCategory', 'Update'),
    canDeleteProductCategory: hasPermission('ProductCategory', 'Delete'),

    canViewShipmentTerm: false,
    canCreateShipmentTerm: hasPermission('ShipmentTerm', 'Create'),
    canUpdateShipmentTerm: hasPermission('ShipmentTerm', 'Update'),
    canDeleteShipmentTerm: hasPermission('ShipmentTerm', 'Delete'),

    canViewSite: false,
    canCreateSite: hasPermission('Site', 'Create'),
    canUpdateSite: hasPermission('Site', 'Update'),
    canDeleteSite: hasPermission('Site', 'Delete'),

    canViewSize: false,
    canCreateSize: hasPermission('Size', 'Create'),
    canUpdateSize: hasPermission('Size', 'Update'),
    canDeleteSize: hasPermission('Size', 'Delete'),

    canViewSupplier: false,
    canCreateSupplier: hasPermission('Supplier', 'Create'),
    canUpdateSupplier: hasPermission('Supplier', 'Update'),
    canDeleteSupplier: hasPermission('Supplier', 'Delete'),

    canViewSupplierType: false,
    canCreateSupplierType: hasPermission('SupplierType', 'Create'),
    canUpdateSupplierType: hasPermission('SupplierType', 'Update'),
    canDeleteSupplierType: hasPermission('SupplierType', 'Delete'),

    canViewUom: false,
    canCreateUom: hasPermission('Uom', 'Create'),
    canUpdateUom: hasPermission('Uom', 'Update'),
    canDeleteUom: hasPermission('Uom', 'Delete'),

    // JobPosition permissions
    canViewJobPosition: false,
    canCreateJobPosition: hasPermission('JobPosition', 'Create'),
    canUpdateJobPosition: hasPermission('JobPosition', 'Update'),
    canDeleteJobPosition: hasPermission('JobPosition', 'Delete'),

    // LightSource permissions
    canViewLightSource: hasPermission('LightSource', 'Read'),
    // Create re-enabled for LightSource specifically — Update/Delete stay disabled site-wide.
    canCreateLightSource: true,
    canUpdateLightSource: hasPermission('LightSource', 'Update'),
    canDeleteLightSource: hasPermission('LightSource', 'Delete'),

    // Section permissions
    canViewSection: hasPermission('Section', 'Read'),
    canCreateSection: hasPermission('Section', 'Create'),
    canUpdateSection: hasPermission('Section', 'Update'),
    canDeleteSection: hasPermission('Section', 'Delete'),

    // CatalogPath permissions — view-only master synced from WFX.
    canViewCatalogPath: hasPermission('CatalogPath', 'Read'),

    // Category permissions
    canViewCategory: hasPermission('Category', 'Read'),
    canCreateCategory: hasPermission('Category', 'Create'),
    canUpdateCategory: hasPermission('Category', 'Update'),
    canDeleteCategory: hasPermission('Category', 'Delete'),

    // ProductGroup permissions
    canViewProductGroup: hasPermission('ProductGroup', 'Read'),
    canCreateProductGroup: hasPermission('ProductGroup', 'Create'),
    canUpdateProductGroup: hasPermission('ProductGroup', 'Update'),
    canDeleteProductGroup: hasPermission('ProductGroup', 'Delete'),

    // SubCategory permissions
    canViewSubCategory: hasPermission('SubCategory', 'Read'),
    canCreateSubCategory: hasPermission('SubCategory', 'Create'),
    canUpdateSubCategory: hasPermission('SubCategory', 'Update'),
    canDeleteSubCategory: hasPermission('SubCategory', 'Delete'),

    // MaterialType permissions
    canViewMaterialType: hasPermission('MaterialType', 'Read'),
    canCreateMaterialType: hasPermission('MaterialType', 'Create'),
    canUpdateMaterialType: hasPermission('MaterialType', 'Update'),
    canDeleteMaterialType: hasPermission('MaterialType', 'Delete'),

    // Component permissions
    canViewComponent: false,
    canCreateComponent: hasPermission('Component', 'Create'),
    canUpdateComponent: hasPermission('Component', 'Update'),
    canDeleteComponent: hasPermission('Component', 'Delete'),

    // ComponentGroup permissions
    canViewComponentGroup: false,
    canCreateComponentGroup: hasPermission('ComponentGroup', 'Create'),
    canUpdateComponentGroup: hasPermission('ComponentGroup', 'Update'),
    canDeleteComponentGroup: hasPermission('ComponentGroup', 'Delete'),
  };
}
